import { FormLabel, Icon, Select, Tooltip } from "@chakra-ui/react";
import { FormControl } from "@chakra-ui/react";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  ModalFooter,
  Button,
} from "@chakra-ui/react";
import MoneyField from "../MoneyInput";
import { FeeModel } from "providers/loans/fee.model";
import { useContext, useState } from "react";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { LuInfo } from "react-icons/lu";
import { FeeAccount } from "./models/fee-account";
import { LoanModel } from "providers/loans/loan.model";
import { LoanContext } from "providers/loan";
import moment from "moment";

interface FeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  saveFee: (fee: FeeModel) => void;
  fee: FeeModel;
  loan: LoanModel;
  accounts: FeeAccount[];
}

export function FeeModal({ isOpen, onClose, saveFee, fee, loan, accounts }: FeeModalProps) {
  const { refetch: loadLoan } = useContext(LoanContext);

  const { showSuccess, showError } = useContext(AlertsContext);
  const [loading, setLoading] = useState(false);

  const [description, setDescription] = useState(fee.description ?? "");
  const [date, setDate] = useState(fee.fee_date ?? "");
  const [amount, setAmount] = useState<string | undefined>(
    fee.fee_amount ? String(fee.fee_amount) : undefined
  );
  const [account, setAccount] = useState<string | null>(fee.account ?? null);

  async function addFee() {
    await apiRequest("/supabase/fees/insert", {
      feeAmount: amount,
      feeDate: date,
      description: description,
      loan: loan!.id,
      account: account,
    });

    showSuccess({
      title: "Fee Added",
      description: "The fee has been added",
    });
  }

  async function updateFee() {
    await apiRequest("/supabase/fees/update", {
      id: fee.id,
      feeAmount: amount,
      feeDate: date,
      description: description,
      loan_id: loan.id,
      account: account,
    });

    showSuccess({
      title: "Saved Changes",
      description: "The fee details have been updated.",
    });
  }

  async function handleSave() {
    if (!amount || !date || !description) {
      showError({
        title: "Please fill all the required fields",
        description: "Please fill all the required fields",
      });

      return;
    }

    if (moment(date, "YYYY-MM-DD").isBefore(moment(loan.funding_date, "YYYY-MM-DD"))) {
      showError({
        title: "Invalid Date",
        description: "Fee date must be greater than or equal to the funding date.",
      });
      return;
    }

    setLoading(true);

    if (fee.id) {
      await updateFee();
    } else {
      await addFee();
    }

    saveFee({
      ...fee,
      description,
      fee_date: date,
      fee_amount: Number(amount),
      account: account,
    });

    onClose();
    await loadLoan();
  }

  const title = fee.id ? "Edit Fee" : "Add Fee";
  const saveButtonText = fee.id ? "Update Fee" : "Add Fee";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* DESCRIPTION */}
          <FormControl mb="12px" isRequired>
            <FormLabel display="flex" alignItems="center">
              Description
              <Tooltip
                hasArrow
                label="Description of the fee."
                aria-label="Description of the fee."
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <Input
              value={description}
              onChange={ev => setDescription(ev.target.value)}
              placeholder="Description"
              variant="primary"
            />
          </FormControl>
          {/* / DESCRIPTION */}

          {/* FEE DATE */}
          <FormControl mb="12px" isRequired>
            <FormLabel display="flex" alignItems="center">
              Fee Date
              <Tooltip
                hasArrow
                label="Date the fee was paid."
                aria-label="Date the fee was paid."
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <Input
              value={date}
              onChange={ev => setDate(ev.target.value)}
              placeholder="Fee Date"
              variant="primary"
              type="date"
            />
          </FormControl>
          {/* / FEE DATE */}

          {/* FEE AMOUNT */}
          <FormControl mb="12px" isRequired>
            <FormLabel display="flex" alignItems="center">
              Fee Amount
              <Tooltip
                hasArrow
                label="Amount of the fee."
                aria-label="Amount of the fee."
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <MoneyField
              value={amount}
              onValueChange={value => setAmount(value)}
              allowNegativeValue={true}
            />
          </FormControl>
          {/* / FEE AMOUNT */}

          {accounts.length > 0 && (
            <>
              {/* FEE ACCOUNT */}
              <FormControl mb="12px">
                <FormLabel display="flex" alignItems="center">
                  Fee Account
                  <Tooltip
                    hasArrow
                    label="Account to debit or credit the fee to."
                    aria-label="Account to debit or credit the fee to."
                    placement="right"
                  >
                    <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                      <LuInfo />
                    </Icon>
                  </Tooltip>
                </FormLabel>
                <Select
                  placeholder="Select an account"
                  value={account?.toString() || ""}
                  onChange={ev => setAccount(ev.target.value)}
                >
                  {accounts.map(account => (
                    <option key={account.code} value={account.code}>
                      {account.description}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {/* / FEE ACCOUNT */}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={loading}>
            {saveButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
